import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper } from '@mui/material';
import { getAllSites } from "../../../selectors/AdminSelectors";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { StyledMultilineTextField, StyledRichTextEditor, StyledTextField } from "../../common/StyledTextField";
import { ArrowBackIos, ArrowForwardIos, Settings, StorefrontOutlined } from "@mui/icons-material";
import { LoadingButton, LoadingTextButton, TooltipIconButton } from "../../common/StyledButtons";
import Colors from "../../../constants/Colors";
import StyledSubHeader from "../../common/StyledSubHeader";
import { isAdmin } from "../../../actions/profile/UserActions";
import { getAllUsersForAdmin, saveEmailForAdmin, sendEmailBlastForAdmin } from "../../../actions/internal/AdminActions";
import { getUserId } from "../../../selectors/UserSelectors";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { StyledLoadingSection } from "../../common/StyledProgressCircle";
import { getAllUsers } from "../../../clients/bettercallkyle/BckApi";
import StyledAvatarChipsTextField from "../../common/StyledAvatarChipTextField";
import StyledPage from "../../common/StyledPage";
import StyledSwitch from "../../common/StyledSwitch";

const DEFAULT_TEMPLATE = `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color: #f4f4f4;">
<tr>
    <td align="center">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="background-color: #fcfcfc; padding: 20px; text-align: center;">
            <tr>
                <td style="padding: 10px 0;">
                <img src="https://consultwithkyle.com/wp-content/uploads/2025/04/bck-abriv-logo-green.png" alt="Better Call Kyle, LLC" style="max-width: 125px; height: auto;" />
                </td>
            </tr>
            <tr>
                <td style="font-size: 24px; color: #353736; padding: 10px 20px; font-weight: bold;">
                    <stong>We're glad you're here!</strong>
                </td>
            </tr>
            <tr>
                <td style="padding: 20px;">
                <hr style="border: none; height: 3px; background-color: #353736; width: 80%;" />
                </td>
            </tr>
            <tr>
                <td style="font-size: 16px; color: #353736; padding: 10px 20px;">
                Hi User!
                </td>
            </tr>
            <tr>
                <td style="font-size: 16px; color: #353736; padding: 10px 20px;">
                    Thanks for creating your account with BCK Consulting. We're excited to have you with us and look forward to helping your business grow online.
                </td>
            </tr>
            <tr>
                <td style="font-size: 16px; color: #353736; padding: 10px 20px;">
                    With your new account, you now have access to your personalized dashboard. From there, you can schedule check-ins, view your website analytics, explore helpful resources, start and track projects and updates, and use tools to keep your online presence working at its best.
                </td>
            </tr>
            <tr>
                <td style="font-size: 16px; color: #353736; padding: 10px 20px;">
                    If you have any questions or need help getting started, feel free to reach out to us at <a href="mailto:team@consultwithkyle.com">team@consultwithkyle.com</a>.
                </td>
            </tr>
            <tr>
                <td style="font-size: 16px; color: #353736; padding: 20px;">
                <a href="https://app.consultwithkyle.com/dahsboard" style="background-color: #10aa00; color: #ffffff; text-decoration: none; font-size: 18px; font-weight: bold; padding: 14px 24px; border-radius: 5px; display: inline-block;">
                    Go To My Dashboard
                </a>
                </td>
            </tr>
            <tr>
                <td style="font-size: 16px; color: #353736; padding: 20px;">
                    Welcome to the BCK community. We’re glad you’re here.
                </td>
            </tr>
            <tr>
                <td style="font-size: 16px; color: #353736; padding: 20px;">
                    Warmly,<br /><br />The BCK Consulting Team
                </td>
            </tr>
            <tr>
                <td style="padding: 20px;">
                <hr style="border: none; height: 1px; background-color: #B5B7B6; width: 30%;" />
                </td>
            </tr>
            <tr>
                <td style="font-size: 10px; color: #959996; text-align: center;">
                <p style="margin: 0;">Better Call Kyle, LLC</p>
                <a href="http://app.consultwithkyle.com/dashboard" style="color: #959996; text-decoration: none;">consultwithkyle.com</a>
                </td>
            </tr>
        </table>
    </td>
</tr>
</table>`

const AdminAddEmailPage = ({ history }) => {

    const formattedUsers = useRef();
    const draftHtml = useRef(DEFAULT_TEMPLATE);

    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [html, setHtml] = useState(DEFAULT_TEMPLATE);
    const [subject, setSubject] = useState('');
    const [recipients, setRecipients] = useState([]);
    const [isBlastEmail, setIsBlastEmail] = useState(false);
    const [errors, setErrors] = useState([]);

    const loadData = async () => {
        if (!isAdmin()) {
            history.replace('/dashboard');
            return;
        }
        try {
            const userId = getUserId();
            const fetchedUsers = await getAllUsersForAdmin(userId);

            formattedUsers.current = fetchedUsers.map(u => ({
                name: `${u.user_personal.first_name} ${u.user_personal.last_name} (${u.user_login_id})`,
                email: u.user_login_id,
                value: u
            })).sort((a, b) => a.name.localeCompare(b.name));

            setUsers(fetchedUsers);
        } catch(err) {
            const e = await err;
            console.error(e);
            showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Uh oh! There was a problem retrieving site information.')
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadData();
    }, [])

    const handleBodyChange = (e) => {
        setHtml(e.target.value)
    }

    const handleSubjectChange = (e) => {
        setSubject(e.target.value)
    }

    const handleRecipientChange = (event, newValue) => {
        setRecipients(newValue)
      };

    const loadingContent = (
        <StyledLoadingSection />
    )

    if (isLoading) return (loadingContent)

    const validateForm = () => {
        const errs = [];
        if (!recipients || recipients.length === 0) errs.push('recipients');
        if (!subject || subject.trim() === '') errs.push('subject');
        if (!html || html.trim() === '') errs.push('body');
        return errs;
    }

    const validateFormForSave = () => {
        const errs = [];
        if (!subject || subject.trim() === '') errs.push('subject');
        return errs;
    }

    const handleSendEmail = async () => {
        try {
            // Verify
            const errs = validateForm();
            setErrors(errs);
            if (errs.length !== 0) {
                return; // If errors exist, return
            }

            setIsSending(true);

            // Prepare Payload
            const emailPayload = {
                ['recipients']: recipients,
                ['subject']: subject,
                body: html
            }

            const options = {
                isBlastEmail
            }

            // Send Payload
            await sendEmailBlastForAdmin(getUserId(), emailPayload, options);
            showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "Email sent successfully!");
            history.replace(`/admin/email-marketing`);
        } catch (ex) {
            showNotificationBar(NOTIFICATION_TYPE.ERROR, "Could not send the email at this time. Please try again in a few moments.");
        } finally {
            setIsSending(false);
        }
        
    }

    const handleSaveEmail = async () => {
        try {
            // Verify
            const errs = validateFormForSave();
            setErrors(errs);
            if (errs.length !== 0) {
                return; // If errors exist, return
            }

            setIsSaving(true);
    
            // Prepare Payload
            const emailPayload = {
                recipients,
                subject,
                body: html
            };
    
            // Save Email
            await saveEmailForAdmin(getUserId(), emailPayload);
    
            showNotificationBar(
                NOTIFICATION_TYPE.SUCCESS,
                'Email saved successfully!'
            );
            history.replace(`/admin/email-marketing`);
        } catch (ex) {
            showNotificationBar(
                NOTIFICATION_TYPE.ERROR,
                'Could not save the email at this time. Please try again in a few moments.'
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Simple templating function
    const renderTemplate = (template, data) => {
        return template.replace(/\{([\w.[\]0-9]+)\}/g, (_, key) => {
          try {
            const keys = key.split(/[\.\[\]]/).filter(Boolean);
            let value = data;
            for (let k of keys) {
              if (value && k in value) {
                value = value[k];
              } else {
                return '';
              }
            }
      
            return value !== undefined && value !== null ? value : '';
          } catch {
            return '';
          }
        });
      };

    const renderedHtml = isBlastEmail ? html : renderTemplate(html, { user: recipients[0] ? recipients[0].value : null });

    // Implement Search and Pagination next
    return (
        <StyledPage>
            <StyledSubHeader title="Create Email" />
            <Grid container spacing={2} sx={{ marginTop: '24px' }}>
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ padding: '16px'}}>
                        <StyledAvatarChipsTextField 
                            error={errors.includes('recipients')}
                            helperText={!isBlastEmail && `${recipients.length}/50 Recipients`}
                            options={formattedUsers.current}
                            label="Recipients"
                            onChange={handleRecipientChange}
                            value={recipients}
                        />
                        <h4 style={{ fontSize: '20px', marginBottom: '5px' }}>Options</h4>
                        <StyledSwitch
                            title="Blast Email"
                            description="This is a general email with no personalization necessary."
                            onChange={(e) => { setIsBlastEmail(e.target.checked) }}
                            checked={isBlastEmail}
                        />
                        <h4 style={{ fontSize: '20px', marginBottom: '5px' }}>Message</h4>
                        <StyledTextField
                            error={errors.includes('subject')}
                            label="Subject"
                            onChange={handleSubjectChange}
                        />
                        <StyledMultilineTextField
                            label="Body"
                            error={errors.includes('body')}
                            textFieldStyle={{
                                textTransform:'none',
                                fontSize:'14px'
                            }}
                            rows={25}
                            onChange={handleBodyChange}
                            value={html}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <h4 style={{ fontSize: '24px' }}>Preview</h4>
                    <Box
                        sx={{
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        padding: 2,
                        backgroundColor: '#fafafa',
                        minHeight: 100,
                        }}
                        dangerouslySetInnerHTML={{ __html: renderedHtml }}
                    />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
                    <LoadingTextButton
                        disabled={isSaving || isSending}
                        isLoading={isSaving}
                        onClick={handleSaveEmail}
                    >
                        Save
                    </LoadingTextButton>
                    <LoadingButton
                        disabled={isSending || isSaving}
                        isLoading={isSending}
                        onClick={handleSendEmail}
                    >
                        Send
                    </LoadingButton>
                </Grid>
            </Grid>
      </StyledPage>
    )
}

export default withRouter(observer(AdminAddEmailPage));

